/**
 * The pure client-side mirrors of PUR-02's quality arithmetic — the server
 * is the authority (the CHECK constraint is the floor); these keep the
 * editor honest between keystrokes.
 */

export type GrnQuality = 'accepted' | 'rejected' | 'partially_accepted';

/**
 * BR-PUR-02-04's resolved rule: Accepted takes everything received,
 * Rejected takes nothing, Partially Accepted takes the typed number (which
 * the editor must show and the server caps strictly below received).
 */
export function acceptedQtyFor(quality: GrnQuality, received: string, typed: string): string {
  if (quality === 'accepted') return received;
  if (quality === 'rejected') return '0';
  return typed;
}

/** S3's counter: it counts LINES, and the label says so (BR-PUR-02-12). */
export function rejectedLineCount(lines: readonly { quality: GrnQuality }[]): number {
  return lines.filter((line) => line.quality === 'rejected').length;
}

/**
 * The Backorder chip + amber row (PUR-02 §5c): received short of what was
 * still OUTSTANDING on the order line — ordered less what earlier posted
 * receipts already took (`remainingQty` on the line, wave 1C). The first
 * build compared against the whole ordered quantity, so the second container
 * of a two-container order was flagged a backorder for the first's share.
 * String compare through Number is display-only — the server never sees it.
 */
export function isBackorder(received: string, remaining: string): boolean {
  const receivedNum = Number(received);
  const remainingNum = Number(remaining);
  if (Number.isNaN(receivedNum) || Number.isNaN(remainingNum)) return false;
  return receivedNum < remainingNum;
}

/**
 * Wave 1C — what is still outstanding on an order line once the OTHER
 * posted receipts are counted: ordered − already received, floored at zero.
 * The server computes the same figure (`remainingQty`); this exists so a
 * grid can keep the three columns honest while the line is being typed.
 */
export function remainingFor(ordered: string, alreadyReceived: string): string {
  const orderedNum = Number(ordered);
  const alreadyNum = Number(alreadyReceived);
  if (Number.isNaN(orderedNum) || Number.isNaN(alreadyNum)) return '0';
  const left = orderedNum - alreadyNum;
  return left > 0 ? String(left) : '0';
}

/**
 * BR-PUR-02-11 — "an actual delivery date later than the expected date must
 * be flagged, so late suppliers can be found." The whole rule as one
 * predicate, used by the receipts list and the editor so the two screens
 * cannot disagree about what late means. ISO dates compare correctly as
 * strings; STRICTLY after, because arriving on the promised day is on time
 * (the spec's own seed is "Expected 08/07 — Actual 09/07, a day late"). An
 * order that names no expected date promised nothing, so nothing is late.
 */
export function receiptIsLate(receipt: {
  expectedDate: string | null;
  receiptDate: string;
}): boolean {
  return receipt.expectedDate !== null && receipt.receiptDate > receipt.expectedDate;
}

/**
 * Wave 5C — a line's value as the grid types it: accepted × unit cost,
 * rounded half-up to the cent, in exact integer arithmetic (quantities carry
 * up to 3 decimals, costs up to 6 — DECISION.md §2.1: no float ever touches
 * a money figure). The server re-snaps every value to the WRITTEN movements
 * at posting; this keeps the footer honest between keystrokes. Unparseable
 * input reads as zero rather than NaN.
 */
export function lineValueOf(acceptedQty: string, unitCost: string): string {
  const qty = scaled(acceptedQty, 3);
  const cost = scaled(unitCost, 6);
  if (qty === null || cost === null) return '0.00';
  // qty × cost is scaled 10^9; the cent is 10^7 of that. Round half-up.
  const cents = (qty * cost + 5_000_000n) / 10_000_000n;
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/** Σ `lineValueOf` over the grid — the "Accepted value" footer (wave 5C). */
export function acceptedValueOf(lines: readonly { acceptedQty: string; unitCost: string }[]): string {
  let cents = 0n;
  for (const line of lines) {
    const value = lineValueOf(line.acceptedQty, line.unitCost);
    cents += BigInt(value.replace('.', ''));
  }
  const negative = cents < 0n;
  const digits = (negative ? -cents : cents).toString().padStart(3, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -2)}.${digits.slice(-2)}`;
}

/** A decimal string at a fixed scale as a BigInt; null when it is not a decimal. */
function scaled(value: string, scale: number): bigint | null {
  const text = value.trim();
  if (!new RegExp(`^-?\\d{1,15}(\\.\\d{1,${scale}})?$`).test(text)) return null;
  const negative = text.startsWith('-');
  const bare = negative ? text.slice(1) : text;
  const [whole = '0', fraction = ''] = bare.split('.');
  const result = BigInt(whole) * 10n ** BigInt(scale) + BigInt(fraction.padEnd(scale, '0'));
  return negative ? -result : result;
}

/**
 * Serial capture helper: the comma-separated box → the wire array. Empty
 * pieces vanish; the server enforces count == accepted units.
 */
export function parseSerials(text: string): string[] {
  return text
    .split(',')
    .map((piece) => piece.trim())
    .filter((piece) => piece !== '');
}
